import { CommonModule } from "@angular/common";
import { Component, EventEmitter, HostBinding, Input, Output, inject } from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import { FormlyModule } from "@ngx-formly/core";
import { NzSelectModule } from "ng-zorro-antd/select";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { UtilitiesService } from "../../../core/services/utilities.service";
import { MaterialModule } from "../../../core/shared/material.module";
import { MessageEnum } from "../../../core/utils/message.enum";
import { DynamicFormComponent } from "../../common/dynamic-form/dynamic-form.component";
import { LoadingComponent } from "../../common/loading/loading.component";

@Component({
  selector: "app-connection-modal",
  standalone: true,
  imports: [
    CommonModule,
    FormlyModule,
    MatInputModule,
    LoadingComponent,
    FormlyBootstrapModule,
    FormsModule,
    ReactiveFormsModule,
    DynamicFormComponent,
    MaterialModule,
    NzSelectModule
  ],
  templateUrl: "./connection-modal.component.html",
  styleUrl: "./connection-modal.component.scss",
})
export class ConnectionModalComponent {
  @Input()
  configs: any = {};
  /** Render the form inside another modal instead of as a stacked popup. */
  @Input()
  @HostBinding("class.embedded")
  embedded = false;
  @Input() saving = false;
  /** Hide Cancel/Add footer when the host modal owns primary actions. */
  @Input() hideFooter = false;
  @Output() saved = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  dynamicFields: any = [];
  connName: string = "";
  modelName: string = "";
  models: any = [];
  provider: any;
  isLoading: boolean = false;
  activeModal = inject(NgbActiveModal, { optional: true });
  dynamicForm: any = new FormGroup({});
  providersFieldsList: any = [];
  options: any = [];
  isActive: boolean = false;

  get isAlm(): boolean {
    return this.configs?.type === "ALM";
  }

  get isDb(): boolean {
    return this.configs?.type === "DATABASE" || this.configs?.type === "DB";
  }

  private readonly aestriks = '************';

  constructor(
    public restService: RestService,
    public toaster: ToasterService,
    private utilitiesServices : UtilitiesService
  ) {}
  ngOnInit() {
    this.getProviderFields(this.configs?.type);
  }

  groupProviders(providers: any[]) {
    // find unique group names
    let groups: any = [];
    providers.forEach((ele: any) => {
      if (ele?.group && !groups.includes(ele.group)) {
        groups.push(ele.group);
      }
    });

    // Group options
    groups.forEach((groupName: any) => {
      let obj = {
        label: groupName,
        type: groupName,
        options: providers.filter((conn: any) => conn?.group === groupName),
      };
      this.options.push(obj);
    });

    // Ungrouped connections
    let ungrouped = providers.filter((ele: any) => !ele?.group);
    if (ungrouped.length) {
      let obj = {
        label: "Ungrouped",
        type: "ungrouped",
        options: ungrouped,
      };
      this.options.push(obj);
    }
  }


  checkStatus() {
    if (this.configs?.isEdit) {
      this.connName = this.configs.model?.connectionName;
      this.modelName = this.configs.model?.meta?.model;
      this.provider = this.resolveProviderValue(
        this.configs.model?.meta?.source || this.configs.model?.meta?.displayName
      );
    } else {
      const firstEnabled = this.providersFieldsList.find(
        (item: any) => !this.isProviderDisabled(item)
      );
      this.provider = firstEnabled?.value ?? this.providersFieldsList[0]?.value;
      if (!this.connName && this.configs?.defaultConnectionName) {
        this.connName = this.configs.defaultConnectionName;
      }
    }
    this.getDynamicFields();
  }
  /**
   * Handles the submission of the dynamic form.
   */
  submit() {
    const trimmedFormValue = this.trimFormValues(this.dynamicForm.getRawValue());
    this.restoreUnchangedMaskedCredentials(trimmedFormValue);
    this.normalizeTableFieldValues(trimmedFormValue);
    this.applyProviderDefaults(trimmedFormValue);

    if(this.configs?.type === "LLM"){
      if(this.provider.trim()== 'google_vertexai'){
        trimmedFormValue.location = trimmedFormValue?.location? trimmedFormValue?.location : "us-central1";
      }
      this.complete({
        value: {
          connectionName: this.connName.trim(),
          meta: {
            model: this.modelName.trim(),
            source: this.provider.trim(),
            credentials: trimmedFormValue
          },
        },
      });
    } else{
    this.complete({
      value: {
        connectionName: this.connName.trim(),
        meta: {
          source: this.provider.trim(),
          ...trimmedFormValue
        },
      },
    });
    }
  }

   /** Apply source-specific default values when the user left optional fields blank. */
   private applyProviderDefaults(formValue: any): void {
    if (!formValue) return;

    const source = (this.provider ?? "").toString().trim().toLowerCase();

    if (this.configs?.type === "LLM" && source === "google_vertexai") {
      formValue.location = formValue?.location ? formValue.location : "us-central1";
    }

    if (
      (this.configs?.type === "DATABASE" || this.configs?.type === "DB") &&
      source === "postgresql" &&
      !String(formValue.schema ?? "").trim()
    ) {
      formValue.schema = "public";
    }
  }

  onCancel(): void {
    if (this.embedded) {
      this.cancelled.emit();
      return;
    }
    this.activeModal?.dismiss("cancel");
  }

  private complete(result: any): void {
    if (this.embedded) {
      this.saved.emit(result);
      return;
    }
    this.activeModal?.close(result);
  }
   /**
   * Table fields are stored as FormGroups of key→value controls.
   * Drop draft/empty keys so credentials get a clean object like:
   * additional_attributes: { provider: "aws_bedrock", model: "..." }
   */
  private normalizeTableFieldValues(formValue: any): void {
    if (!formValue) return;

    this.dynamicFields
      .filter((field: any) => field.type === "table" || field.type === "table-input")
      .forEach((field: any) => {
        const raw = formValue[field.key];
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
          formValue[field.key] = {};
          return;
        }

        const cleaned: Record<string, any> = {};
        Object.keys(raw).forEach((key) => {
          if (!key || key.startsWith("new_key_")) return;
          cleaned[key] = raw[key];
        });
        formValue[field.key] = cleaned;
      });
  }

  /**
   * Password fields are shown as asterisks in edit mode. If the user did not
   * change them, put back the original masked values from the selected
   * connection (e.g. `"<ACCESS_KEY>"`) instead of sending `************`.
   */
  private restoreUnchangedMaskedCredentials(formValue: any): void {
    if (!this.configs?.isEdit || !formValue) {
      return;
    }

    const originalCreds = this.configs.model?.meta?.credentials || {};
    this.dynamicFields
      .filter((field: any) => field.type === 'password-input')
      .forEach((field: any) => {
        if (formValue[field.key] === this.aestriks && originalCreds.hasOwnProperty(field.key)) {
          formValue[field.key] = originalCreds[field.key];
        }
      });
  }

  trimFormValues(obj: any): any {
    if (typeof obj !== 'object' || obj === null) return obj;
  
    return Array.isArray(obj)
      ? obj.map((item) => this.trimFormValues(item))
      : Object.keys(obj).reduce((acc, key) => {
          const value = obj[key];
          acc[key] =
            typeof value === 'string' ? 
            value.trim().replace(/\\n/g, '\n')
              : this.trimFormValues(value);
          return acc;
        }, {} as any);
  }
  

  /**
   * Normalize provider field definitions from the API into dynamic-form shape.
   * - `dropdown` → `select`
   * - `table-input` → `table` (key/value editable table)
   * - options `{ key, value }` → `{ value, label }`
   */
  private normalizeProviderField(field: any): any {
    const normalized = { ...field };

    if (normalized.type === "dropdown") {
      normalized.type = "select";
    }

    if (normalized.type === "table-input") {
      normalized.type = "table";
      // Key/value tables need full width for usability
      normalized.wrapperClass = normalized.wrapperClass === "col-md-6" ? "col-md-12" : (normalized.wrapperClass || "col-md-12");
    }

    if (Array.isArray(normalized.options)) {
      normalized.options = normalized.options.map((opt: any) => {
        if (opt?.key != null && opt.label == null) {
          return {
            value: opt.key,
            label: opt.value,
            disabled: opt.disabled,
          };
        }
        return opt;
      });
    }

    return normalized;
  }

  /** Whether a conditional field should show given the current driver field defaults. */
  private isFieldInitiallyVisible(field: any, fields: any[]): boolean {
    if (!field.visibleIf) return true;
    const driver = fields.find((f: any) => f.key === field.visibleIf.key);
    const current = driver?.value;
    if (Array.isArray(field.visibleIf.values)) {
      return field.visibleIf.values.includes(current);
    }
    return current === field.visibleIf.value;
  }

  getDynamicFields() {
    const providerConfig = this.findProvider(this.provider);
    let fields: any[] = (providerConfig?.fields || []).map((f: any) =>
      this.normalizeProviderField(f)
    );
    this.models = providerConfig?.models || [];

    // Apply conditionalFields: build visibleIf for each field listed under any condition value.
    // A field may appear under multiple condition values (e.g. region_name for access_key + bearer).
    const conditionalFieldsConfig: any[] = providerConfig?.conditionalFields || [];
    if (conditionalFieldsConfig.length > 0) {
      const visibleIfMap: Record<string, { key: string; values: any[] }> = {};

      conditionalFieldsConfig.forEach((conditional: any) => {
        Object.entries(conditional.conditions || {}).forEach(([conditionValue, fieldKeys]) => {
          // Parse string boolean keys to their actual boolean type
          let parsedValue: any = conditionValue;
          if (conditionValue === "true") parsedValue = true;
          else if (conditionValue === "false") parsedValue = false;

          (fieldKeys as string[]).forEach((fieldKey: string) => {
            if (!visibleIfMap[fieldKey]) {
              visibleIfMap[fieldKey] = { key: conditional.key, values: [] };
            }
            if (!visibleIfMap[fieldKey].values.includes(parsedValue)) {
              visibleIfMap[fieldKey].values.push(parsedValue);
            }
          });
        });
      });

      fields = fields.map((field: any) => {
        const vis = visibleIfMap[field.key];
        if (!vis) return field;
        return {
          ...field,
          visibleIf:
            vis.values.length === 1
              ? { key: vis.key, value: vis.values[0] }
              : { key: vis.key, values: vis.values },
        };
      });
    }

    // Unconditional fields first (e.g. auth_type), then conditional fields
    fields = [
      ...fields.filter((field: any) => !field.visibleIf),
      ...fields.filter((field: any) => !!field.visibleIf),
    ];

    fields = fields.filter((field: any) => !field.hidden && !this.isFieldHidden(field));
    this.expandOrphanHalfWidthField(fields);
     // Seed table field values from existing credentials when editing
     if (this.configs?.isEdit && this.configs?.type === "LLM") {
      const credentials = this.configs.model?.meta?.credentials || {};
      fields = fields.map((field: any) => {
        if (field.type === "table" && credentials[field.key] && typeof credentials[field.key] === "object") {
          return { ...field, value: credentials[field.key] };
        }
        return field;
      });
    }

    this.dynamicFields = fields;
    this.dynamicForm.reset();

    // Clear existing controls
    Object.keys(this.dynamicForm.controls).forEach((key) => {
      this.dynamicForm.removeControl(key);
    });

    // Add new controls; initialize checkbox fields with false so visibility check works correctly
    this.dynamicFields.forEach((field: any) => {
      if (field.type === "table" || field.type === "table-input") {
        return;
      }
      const initialValue = this.resolveFieldInitialValue(field);
      const initiallyVisible = this.isFieldInitiallyVisible(field, this.dynamicFields);
      const control = new FormControl(
        initialValue,
        initiallyVisible && field.validations?.required ? Validators.required : []
      );

      if (field.isDisabled || field.disabled) {
        control.disable();
      }

      this.dynamicForm.addControl(field.key, control);
    });

    if (this.configs?.isEdit) {
      if(this.configs?.type === "LLM"){
        const credentials = this.configs.model?.meta?.credentials || {};
        const maskedPatch: any = {};
        this.dynamicFields.forEach((field: any) => {
          if (field.type === 'password-input' && credentials.hasOwnProperty(field.key)) {
            maskedPatch[field.key] = this.aestriks;
            // <api_key>
            field.isMasked = true;
          }
        });
        // Avoid patching table FormGroups with a plain object before setupTable runs
        const nonTablePatch = Object.keys(credentials).reduce((acc: any, key: string) => {
          const field = this.dynamicFields.find((f: any) => f.key === key);
          if (field?.type === "table" || field?.type === "table-input") return acc;
          acc[key] = credentials[key];
          return acc;
        }, {});
        this.dynamicForm.patchValue({ ...nonTablePatch, ...maskedPatch });
      } else{
        this.dynamicForm.patchValue(this.configs.model?.meta);
      }
    }
  }

  /** Disabled checkboxes from API config are informational and should stay checked. */
  private resolveFieldInitialValue(field: any): any {
    if (field.type === 'checkbox') {
      if (field.disabled || field.isDisabled) {
        return true;
      }
      return field.value !== undefined ? field.value : false;
    }
    return field.value !== undefined ? field.value : '';
  }

  isProviderDescriptionHidden() {
    return (
      this.dynamicForm?.get("auth_type")?.value === "irsa" ||
      !!this.dynamicFields.find((field: any) => field.key === "enable_irsa")?.value
    );
  }

  isProviderSelected(item: { value?: string; label?: string }): boolean {
    if (!item || !this.provider) {
      return false;
    }
    const selected = this.normalizeProviderKey(this.provider);
    return (
      this.normalizeProviderKey(item.value) === selected ||
      this.normalizeProviderKey(item.label) === selected
    );
  }

  isProviderDisabled(item: { value?: string; label?: string }): boolean {
    const disabled = this.configs?.disabledProviders;
    if (!Array.isArray(disabled) || !item) {
      return false;
    }
    const value = this.normalizeProviderKey(item.value);
    const label = this.normalizeProviderKey(item.label);
    return disabled.some((entry: string) => {
      const key = this.normalizeProviderKey(entry);
      return key === value || key === label;
    });
  }

  providerDisabledHint(item: { value?: string; label?: string }): string {
    if (this.configs?.isEdit || !this.isProviderDisabled(item)) {
      return "";
    }
    return this.configs?.disabledProviderHint || "Not available";
  }

  get hideConnectionName(): boolean {
    return this.isFieldHidden("connectionName");
  }

  get canSave(): boolean {
    return !!this.provider && !this.dynamicForm.invalid && (this.hideConnectionName || !!this.connName);
  }

  isFieldHidden(field: { key?: string; name?: string } | string): boolean {
    const hidden = this.configs?.hideFields;
    if (!Array.isArray(hidden) || !hidden.length) {
      return false;
    }
    const key = typeof field === "string" ? field : field?.key;
    const name = typeof field === "string" ? field : field?.name;
    return hidden.includes(key) || hidden.includes(name);
  }

  /** If hiding a paired field leaves one col-md-6, stretch it to full width. */
  private expandOrphanHalfWidthField(fields: any[]): void {
    const halfWidth = fields.filter((field: any) =>
      String(field.wrapperClass || "").includes("col-md-6")
    );
    if (halfWidth.length % 2 === 1) {
      halfWidth[0].wrapperClass = "col-md-12";
    }
  }

  onSelectChange(event: string) {
    const selected = this.findProvider(event);
    if (this.isProviderDisabled(selected)) {
      return;
    }
    this.provider = selected?.value ?? event;
    this.getDynamicFields();
  }

  onModelSelectChange() {}

  getProviderFields(type: string) {
    this.isLoading = true;

    this.restService.getLLMProviderFields(type).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.groupProviders(response?.data);
        this.providersFieldsList = response?.data;
        this.checkStatus();
      },
      error: (error: any) => {
        this.isLoading = false;
        this.toaster.pop({
          type: "error",
          text: MessageEnum.ERROR_GETTING_PROVIDER_FIELDS,
        });
      },
    });
  }

  private resolveProviderValue(source?: string): string {
    return this.findProvider(source)?.value ?? source ?? "";
  }

  private findProvider(source?: string): any {
    const key = this.normalizeProviderKey(source);
    if (!key) {
      return undefined;
    }
    return this.providersFieldsList.find(
      (item: any) =>
        this.normalizeProviderKey(item.value) === key ||
        this.normalizeProviderKey(item.label) === key
    );
  }

  private normalizeProviderKey(value?: string): string {
    return (value || "").toLowerCase().replace(/[\s_-]/g, "");
  }

  private getHardcodedDbProviders(): any[] {
    return [
      {
        label: "MySQL",
        value: "mysql",
        fields: [
          {
            key: "host",
            name: "Host / Server",
            type: "text-input",
            placeholder: "Ex:db-demo.example.com",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "port",
            name: "Port",
            type: "text-input",
            placeholder: "Ex:3306",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "databaseName",
            name: "Database Name",
            type: "text-input",
            placeholder: "Ex:customer_data",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "username",
            name: "Username",
            type: "text-input",
            placeholder: "Ex:demo_user",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "password",
            name: "Password",
            type: "password-input",
            placeholder: "*************",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "enableSsl",
            name: "Enable SSL",
            type: "checkbox",
            hideLabel: true,
            tooltip: "SSL is a security setting for a database connection. It encrypts the communication between your application and the database.",
            wrapperClass: "col-md-12",
          },
        ],
      },
      {
        label: "PostgreSQL",
        value: "postgresql",
        fields: [
          {
            key: "host",
            name: "Host / Server",
            type: "text-input",
            placeholder: "Ex:db-demo.example.com",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "port",
            name: "Port",
            type: "text-input",
            placeholder: "Ex:5432",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "databaseName",
            name: "Database Name",
            type: "text-input",
            placeholder: "Ex:customer_data",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "username",
            name: "Username",
            type: "text-input",
            placeholder: "Ex:demo_user",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "password",
            name: "Password",
            type: "password-input",
            placeholder: "*************",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "schema",
            name: "Schema",
            type: "text-input",
            placeholder: "Ex:public",
            wrapperClass: "col-md-6",
            validations: { required: true },
          },
          {
            key: "enableSsl",
            name: "Enable SSL",
            type: "checkbox",
            hideLabel: true,
            tooltip: "SSL (Secure Sockets Layer) is a security setting for a database connection. It encrypts the communication between your application and the database.",
            wrapperClass: "col-md-12",
          },
        ],
      }
    ];
  }
}
